import { announce } from "@kovernow/flows/announce";
import {
  authenticateFailure,
  kycQueryKeys,
  MARITAL_STATUS_CODES,
  maskNric,
  singpassRecovery,
  validateVerifyFields,
  verifyPrefill,
  type AuthenticateFailure,
  type MyinfoPerson,
  type VerifyField,
  type VerifyFields,
} from "@kovernow/flows/kyc";
import { profileQueryKeys } from "@kovernow/flows/profile";
import { toRecoveryAction, type RecoveryAction } from "@kovernow/flows/recovery";
import { Button } from "@kovernow/react-ui/components/button";
import { DescriptionList } from "@kovernow/react-ui/components/description-list";
import { InfoBanner } from "@kovernow/react-ui/components/info-banner";
import { Label } from "@kovernow/react-ui/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@kovernow/react-ui/components/select";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { FormField } from "../../components/FormField";
import { Page } from "../../components/Page";
import { Recovery } from "../../components/Recovery";
import { useServices, useSession } from "../../core/app-context";
import { useProfile } from "../../core/queries";
import { verifyReturnTo } from "../../core/safe-path";
import { markReturned, wasReturned } from "../../core/singpass-return";
import { useT } from "../../core/use-t";
import {
  readStoredReturnTo,
  useBfcacheReset,
  VerifiedBanner,
  VerifyLoading as Loading,
  writeStoredReturnTo,
} from "./verify-shared";

/**
 * W-7 `/account/verify` (SG): verify with SingPass by full-page redirect and
 * return. Start → SingPass → back here with a one-time token (stripped from
 * the URL at load, memory only) → Myinfo read once → the identity fields
 * shown read-only, email/mobile/marital status editable → `POST
 * /usersPrivate/authenticate` with the token (the server sets
 * `isAuthenticated`) → `returnTo`. A SingPass failure is a RecoveryView whose
 * "Try again" restarts SingPass with the stored `returnTo`; an authenticate
 * that got no usable answer keeps the token so the form can be sent again.
 */
export function VerifyIdentity() {
  const t = useT();
  const { singpassApi, kycApi, singpassReturn, leavePage, format } = useServices();
  const { user } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const profile = useProfile();

  // Read once (StrictMode-safe), then clear the in-memory return.
  const [captured] = useState(() => singpassReturn.peek());
  // The marker only counts with no captured return AND no ?returnTo= (every
  // caller's fresh entry carries one); a stale marker never hijacks a new visit.
  const [returned] = useState(() => !!captured || (!params.has("returnTo") && wasReturned()));
  useEffect(() => singpassReturn.clear(), [singpassReturn]);

  // A return reads the stored target (re-validated); a fresh entry reads ?returnTo=.
  const [returnTo] = useState(() => verifyReturnTo(returned ? readStoredReturnTo() : params.get("returnTo")));

  const [failure, setFailure] = useState<RecoveryAction | null>(() => {
    if (captured?.error) return singpassRecovery(captured.error);
    if (returned && !captured?.token) return singpassRecovery("session_lost");
    return null;
  });
  // An error return never spends a token that came with it.
  const [token, setToken] = useState(captured?.error ? undefined : captured?.token);
  const [starting, setStarting] = useState(false);
  const startingRef = useRef(false);

  // Back from SingPass can restore this page from the bfcache: un-stick Start.
  const unstick = useCallback(() => {
    startingRef.current = false;
    setStarting(false);
  }, []);
  useBfcacheReset(unstick);

  const verified = profile.data?.isAuthenticated === true;

  const session = useQuery({
    queryKey: kycQueryKeys.singpassSession(token ?? ""),
    queryFn: () => singpassApi!.getSession(token!),
    enabled: !!token && !!singpassApi && profile.isSuccess && !verified,
    retry: false,
    staleTime: Infinity,
    // Myinfo (full NRIC) only lives while this screen does.
    gcTime: 0,
  });

  useEffect(() => {
    if (session.isError) {
      setFailure(singpassRecovery(session.error));
      setToken(undefined);
    }
  }, [session.isError, session.error]);

  useEffect(() => {
    if (verified) {
      // Nothing left to resume.
      markReturned(false);
      writeStoredReturnTo(null);
    }
  }, [verified]);

  const start = async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    setFailure(null);
    writeStoredReturnTo(returnTo);
    markReturned(false);
    try {
      if (!singpassApi) throw new Error("SingPass service not configured");
      const { redirectUrl } = await singpassApi.init();
      // The page leaves; the button stays busy until it does.
      leavePage(redirectUrl);
    } catch (err) {
      startingRef.current = false;
      setStarting(false);
      setFailure(singpassRecovery(err));
    }
  };

  let body;
  if (profile.isPending) {
    body = <Loading label={t("common.loading")} />;
  } else if (profile.isError) {
    body = (
      <div data-testid="verify-profile-error">
        <Recovery action={toRecoveryAction(profile.error)} onRetry={() => void profile.refetch()} retryable />
      </div>
    );
  } else if (verified) {
    body = <VerifiedBanner returnTo={returnTo} />;
  } else if (failure) {
    body = (
      <div data-testid="verify-failure" data-error={failure.errorCode}>
        <Recovery
          action={failure}
          onRetry={() => void start()}
          handlers={{ retry: () => void start() }}
        />
      </div>
    );
  } else if (token && session.data) {
    body = (
      <ReviewForm
        person={session.data}
        format={format}
        onDone={async (fields) => {
          try {
            await kycApi.authenticate({ uid: user?.id ?? "", token, fields });
          } catch (err) {
            // A lost response can hide a committed write: the refreshed profile then shows verified.
            await queryClient.invalidateQueries({ queryKey: profileQueryKeys.all });
            const f = authenticateFailure(err);
            if (f.resubmit) return f;
            setToken(undefined);
            queryClient.removeQueries({ queryKey: kycQueryKeys.all });
            setFailure(f.action);
            return;
          }
          queryClient.removeQueries({ queryKey: kycQueryKeys.all });
          await queryClient.invalidateQueries({ queryKey: profileQueryKeys.all });
          announce(t("verify.verified"));
          writeStoredReturnTo(null);
          markReturned(false);
          navigate(returnTo, { replace: true });
        }}
      />
    );
  } else if (token) {
    body = <Loading label={t("verify.loadingMyinfo")} />;
  } else {
    body = (
      <div className="flex flex-col gap-6" data-testid="verify-start">
        <p>{t("verify.intro")}</p>
        <div>
          <Button
            type="button"
            size="touch"
            variant="cta"
            aria-busy={starting || undefined}
            aria-disabled={starting || undefined}
            onClick={() => void start()}
            data-testid="verify-start-button"
          >
            {starting ? t("verify.starting") : t("verify.start")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Page titleKey="nav.verifyIdentity" flowKey="nav.account">
      {body}
    </Page>
  );
}

function ReviewForm({
  person,
  format,
  onDone,
}: {
  person: MyinfoPerson;
  format: ReturnType<typeof useServices>["format"];
  /** Resolves a resubmittable failure to show inline; otherwise the screen moves on. */
  onDone: (fields: VerifyFields) => Promise<Extract<AuthenticateFailure, { resubmit: true }> | void>;
}) {
  const t = useT();
  const id = useId();
  const [fields, setFields] = useState<VerifyFields>(() => verifyPrefill(person));
  const [touched, setTouched] = useState<Partial<Record<VerifyField, boolean>>>({});
  const [showAll, setShowAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Extract<AuthenticateFailure, { resubmit: true }> | null>(null);
  const submittingRef = useRef(false);
  const errors = validateVerifyFields(fields);
  const errorFor = (f: VerifyField) => ((showAll || touched[f]) && errors[f] ? t(errors[f]!) : undefined);
  const blur = (f: VerifyField) => () => setTouched((s) => ({ ...s, [f]: true }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setShowAll(true);
    if (Object.keys(errors).length > 0 || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      setSubmitError((await onDone(fields)) ?? null);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const notProvided = t("verify.notProvided");
  const dob = person.dob ? format.date(person.dob) || person.dob : "";
  const nric = maskNric(person.nric);
  const identity = [
    { id: "name", term: t("verify.fields.name"), value: person.fullName },
    { id: "nric", term: t("verify.fields.nric"), value: nric, mono: true },
    { id: "dob", term: t("verify.fields.dob"), value: dob },
    { id: "nationality", term: t("verify.fields.nationality"), value: person.nationalityDesc },
    { id: "address", term: t("verify.fields.address"), value: person.address },
  ];

  const maritalError = errorFor("maritalStatus");
  const maritalLabelId = `${id}-marital-label`;
  const maritalTriggerId = `${id}-marital`;
  const maritalErrorId = `${id}-marital-error`;

  return (
    <form noValidate onSubmit={(e) => void submit(e)} className="flex flex-col gap-8" data-testid="verify-review">
      <section aria-labelledby={`${id}-myinfo`} className="flex flex-col gap-3">
        <h2 id={`${id}-myinfo`} className="text-lg font-semibold">
          {t("verify.myinfoTitle")}
        </h2>
        <p className="text-sm text-muted-foreground">{t("verify.myinfoNote")}</p>
        <DescriptionList
          data-testid="verify-myinfo"
          items={identity.map((f) => ({
            id: f.id,
            term: f.term,
            description: f.value || notProvided,
            mono: f.mono && !!f.value,
            muted: !f.value,
          }))}
        />
      </section>
      <section aria-labelledby={`${id}-contact`} className="flex flex-col gap-4">
        <h2 id={`${id}-contact`} className="text-lg font-semibold">
          {t("verify.contactTitle")}
        </h2>
        <p className="text-sm text-muted-foreground">{t("verify.contactNote")}</p>
        <FormField
          label={t("verify.fields.email")}
          type="email"
          autoComplete="email"
          required
          aria-required
          value={fields.kycEmail}
          onChange={(e) => setFields((f) => ({ ...f, kycEmail: e.target.value }))}
          onBlur={blur("kycEmail")}
          error={errorFor("kycEmail")}
          data-testid="verify-email"
        />
        <FormField
          label={t("verify.fields.mobile")}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          required
          aria-required
          value={fields.mobile}
          onChange={(e) => setFields((f) => ({ ...f, mobile: e.target.value }))}
          onBlur={blur("mobile")}
          error={errorFor("mobile")}
          data-testid="verify-mobile"
        />
        <div className="flex flex-col gap-1.5">
          <Label id={maritalLabelId} htmlFor={maritalTriggerId}>
            {t("verify.fields.marital")}
          </Label>
          <Select
            value={fields.maritalStatus || undefined}
            onValueChange={(v) => {
              setFields((f) => ({ ...f, maritalStatus: v }));
              setTouched((s) => ({ ...s, maritalStatus: true }));
            }}
          >
            <SelectTrigger
              id={maritalTriggerId}
              aria-labelledby={`${maritalLabelId} ${maritalTriggerId}`}
              aria-describedby={maritalError ? maritalErrorId : undefined}
              aria-invalid={maritalError ? true : undefined}
              aria-required
              className="min-h-11 w-full sm:w-72"
              data-testid="verify-marital"
            >
              <SelectValue placeholder={t("verify.maritalPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {MARITAL_STATUS_CODES.map((m) => (
                <SelectItem key={m.code} value={m.code}>
                  {t(m.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {maritalError ? (
            <p id={maritalErrorId} className="text-sm font-semibold text-status-error">
              {maritalError}
            </p>
          ) : null}
        </div>
      </section>
      {submitError ? (
        <InfoBanner
          role="alert"
          tone="warning"
          title={t(submitError.messageKey)}
          body={submitError.correlationId ? t("errors.reference", { id: submitError.correlationId }) : undefined}
          data-testid="verify-submit-error"
        />
      ) : null}
      <div>
        <Button
          type="submit"
          size="touch"
          variant="cta"
          aria-busy={submitting || undefined}
          aria-disabled={submitting || undefined}
          data-testid="verify-submit"
        >
          {submitting ? t("verify.submitting") : t("verify.submit")}
        </Button>
      </div>
    </form>
  );
}
